/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer } from "react";
import { useOutletContext, useLocation, useNavigate, useParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { Save, ArrowBigRight, ArrowBigLeft } from "lucide-react";
import { useAdminGetCompetition, useAdminUpdateCompetition } from "@/services/competitionService";
import { useAdminGetSkillsSWR } from "@/services/skillService";
import { compareTwoArrays } from "@/utils/compareTwoArrays";



const initialState = {
    initialApiPayload: {
        name: '',
        code: '',
        active: true,
        default: false,
        skill_ids: [],
        archived: false,
    },
    apiPayload: {
        name: '',
        code: '',
        active: true,
        default: false,
        skill_ids: [],
        archived: false,
    },
    error_message: "",
    isLoading: false
}



const ACTIONS = {
    SET_INITIAL_API_PAYLOAD: 'setInitialApiPayload',
    SET_API_PAYLOAD_FIELD: 'setApiPayloadField',
    ADD_SKILL_ID: 'addSkillId',
    REMOVE_SKILL_ID: 'removeSkillId',
    SET_ERROR_MESSAGE: 'setErrorMessage',
    SET_IS_LOADING: 'setIsLoading',
}



const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_INITIAL_API_PAYLOAD:
            return { ...state, apiPayload: action.payload, initialApiPayload: action.payload, error_message: null }
        case ACTIONS.SET_API_PAYLOAD_FIELD:
            if (action.subtype == "archived" && action.payload === true) {
                return { ...state, apiPayload: { ...state.apiPayload, active: false, default: false, [action.subtype]: action.payload }, error_message: null }
            } else {
                return { ...state, apiPayload: { ...state.apiPayload, [action.subtype]: action.payload }, error_message: null }
            }
        case ACTIONS.ADD_SKILL_ID:
            return { ...state, apiPayload: { ...state.apiPayload, skill_ids: [...state.apiPayload.skill_ids, Number(action.payload)] } }
        case ACTIONS.REMOVE_SKILL_ID:
            return { ...state, apiPayload: { ...state.apiPayload, skill_ids: state.apiPayload.skill_ids.filter((skill_id) => skill_id != action.payload) } }
        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, error_message: action.payload }
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload }
        default:
            throw new Error("Unknown action.type " + action.type);
    }
}

export default function Add() {

    const [ , setPageTitle,  , setShowBackButton] = useOutletContext()
    const [state, dispatch] = useReducer(reducer, initialState)

    const navigate = useNavigate()
    const location = useLocation()
    const {id} = useParams()

    const {data: skills} = useAdminGetSkillsSWR(true)
    const adminGetCompetition = useAdminGetCompetition()
    const adminUpdateCompetition = useAdminUpdateCompetition()



    /* On met à jour le titre */
    useEffect(() => {
        setPageTitle("Modification d'une compétition")
        setShowBackButton(true)

        return () => {
            setPageTitle("Titre")
            setShowBackButton(false)
        }
    }, [setPageTitle, setShowBackButton])



    /* On met à jour le titre du formulaire lorsque les informations du competition sont disponibles */
    useEffect(() => {
        setPageTitle(`Modification d'une compétition : ${state.initialApiPayload.name}`)
    }, [state.initialApiPayload])



    /* On récupère le competition automatiquement lorsque l'id est disponible */
    useEffect(() => {
        const getCompetition = async () => {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

            try {
                const competition = await adminGetCompetition(id)
                competition.skill_ids = competition.skills.map((skill) => skill.id)
                competition.default = competition.default ?? false 
                dispatch({ type: ACTIONS.SET_INITIAL_API_PAYLOAD, payload: competition })
            } catch (error) {
                console.error('admin/competitions/[id]::getCompetition::error', error)
                if (isAxiosError(error)) {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
                } else {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
                }
            } finally {
                dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
            }
        }

        getCompetition()
    }, [id])



    /* Gestion des changements de champs du formulaire */
    const handleChange = (e) => {
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

        dispatch({ type: ACTIONS.SET_API_PAYLOAD_FIELD, 
            subtype: e.target.name, 
            payload: e.target.type == 'checkbox' ? e.target.checked : e.target.value
        })     
    }



    /* Soumission du formulaire */
    const handleSubmit = async (e) => {
        e.preventDefault()

        if(!state.apiPayload.name || !state.apiPayload.code ) {
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Veuillez remplir tous les champs obligatoires" });
            return 
        }

        let payload = Object.fromEntries(Object.entries(state.apiPayload).filter(([key, value]) => {
            if (key == 'skill_ids')
                return !compareTwoArrays(value, state.initialApiPayload.skill_ids)
            else
                return value != state.initialApiPayload[key]
        }))

        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });

        try {
            await adminUpdateCompetition(id, payload)
            navigate(location.state?.from ?? "/admin/skills")
        } catch (error) {
            console.error('admin/competitions/add::handleSubmit::error', error)
            if (error?.response?.status === 412) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Erreur de validation en base de données (code déjà utilisé, contrainte non respectée)" });
            } else if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }





    return (
        <>
            <h2 className="text-secondary text-lg flex-none content-center mb-4">
                Paramètres de la compétition
            </h2>

            <form className="flex flex-col gap-4" onSubmit={(e) => handleSubmit(e)}>

                {/* Premiere ligne : code*/}
                <div className="md:w-[30%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="code">
                            Code <span className="text-red-500">*</span>
                        </label>
                        <input type="text" 
                            className={`w-full border rounded-md ${state?.apiPayload?.code != state?.initialApiPayload?.code ? "border-red-600" : "border-secondary"}`}
                            id="code" name="code" 
                            value={state.apiPayload.code} 
                            onChange={handleChange}
                            required
                            />
                    </div>
                </div>
                {/* Deuxieme ligne : nom */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="name">
                            Nom <span className="text-red-500">*</span>
                        </label>
                        <input type="text" 
                            className={`w-full border rounded-md ${state?.apiPayload?.name != state?.initialApiPayload?.name ? "border-red-600" : "border-secondary"}`}
                            id="name" name="name" 
                            value={state.apiPayload.name} 
                            onChange={handleChange}
                            required
                            />
                    </div>
                </div>

                {/* Ligne : Active */}
                <div className="md:w-[100%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-row gap-2 flex-auto">
                        <input type="checkbox" 
                            className={`w-6 h-6 border rounded-md border-secondary`}
                            id="active" name="active" 
                            checked={state.apiPayload.active} 
                            onChange={handleChange}
                            />
                        <label 
                            htmlFor="active"
                            className={`${state?.apiPayload?.active !== state?.initialApiPayload?.active ? "text-red-600" : "text-primary"}`} >
                            Activée <span className="text-xs">Si désactivée, les rôles non modérateur des groupes de forum associés passent en lecture, mais les droits spécifiques aux forums ou liés à d&apos;autres groupes resteront. ACTION IRREVERSIBLE</span>
                        </label>
                    </div>
                </div>

                {/* Ligne : Default */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-row gap-2 flex-auto">
                        <input type="checkbox" 
                            className={`w-6 h-6 border rounded-md`}
                            id="default" name="default" 
                            checked={state.apiPayload.default} 
                            onChange={handleChange}
                            />
                        <label  htmlFor="default"
                            className={`${state?.apiPayload?.default !== state?.initialApiPayload?.default ? "text-red-600" : "text-primary"}`}>
                            Compétition par défaut 
                            { state?.apiPayload?.default !== state?.initialApiPayload?.default && state?.apiPayload?.default === true && (
                                    <span> (désactivera l&apos;autre compétition actuellement par défaut)</span>
                                )
                            }
                        </label>
                    </div>
                </div>

                {/* Ligne : Archivé */}
                <div className="md:w-[100%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-row gap-2 flex-auto">
                        <input type="checkbox" 
                            className={`w-6 h-6 border rounded-md border-secondary`}
                            id="archived" name="archived" 
                            checked={state.apiPayload.archived} 
                            onChange={handleChange}
                            />
                        <label 
                            htmlFor="archived"
                            className={`${state?.apiPayload?.archived !== state?.initialApiPayload?.archived ? "text-red-600" : "text-primary"}`} >
                            Archivée <span className="text-xs">Si archivée, les rôles non modérateur des groupes de forum associés seront supprimés, mais les droits spécifiques aux forums ou liés à d&apos;autres groupes resteront. ACTION IRREVERSIBLE</span>
                        </label>
                    </div>
                </div>

                {/* Ligne : gestion des métiers associés */}
                <div className="md:w-full flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex-1 flex flex-col gap-2 bg-slate-50 border border-secondary rounded-md px-2 py-1 ">
                        <h3 className="text-secondary">Métiers disponibles</h3>
                        {skills?.filter((skill) => !state.apiPayload.skill_ids.includes(skill.id)).map((skill, i) => {
                            return (
                                <div key={i} 
                                    className="w-full flex flex-row justify-between hover:bg-slate-200 hover:rounded-md px-1 cursor-pointer hover:text-green-600"
                                    onClick={(e) => {e.preventDefault();dispatch({ type: ACTIONS.ADD_SKILL_ID, payload: skill.id })}}
                                >
                                    <span className="text-base">{skill.code} - {skill.name}</span>
                                    <ArrowBigRight size={24} className="" />
                                </div>
                            )
                        })}
                    </div>
                    <div className="flex flex-col gap-2 flex-1  bg-slate-50 border border-secondary rounded-md px-2 py-1 ">
                    <h3 className="text-secondary">Métiers associés</h3>
                        {skills && state?.apiPayload?.skill_ids?.map((id, i) => {
                            const skill = skills.find((skill) => skill.id === id)
                            return (
                                <div key={i} 
                                    className="w-full flex flex-row justify-left hover:bg-slate-200 hover:rounded-md hover:text-red-600 px-1 cursor-pointer"
                                    onClick={(e) => {e.preventDefault();dispatch({ type: ACTIONS.REMOVE_SKILL_ID, payload: skill.id })}}
                                >
                                    <ArrowBigLeft size={24} className="text-primary hover:text-blue-600" />
                                    <span className="text-base">{skill.code} - {skill.name}</span>
                                    
                                </div>
                            )
                        })}
                    </div>
                </div>  
                <div className="w-[50%] gap-5 flex flex-row items-center">
                    
                    <button className="flex gap-2 items-center border border-blue-500 rounded-md px-2 py-1 text-base hover:bg-blue-200"
                        onClick={(e) => handleSubmit(e)}
                        disabled={state.isLoading}
                    >
                        <Save size={24} className="text-primary" />
                        Sauvegarder
                    </button>
                    
                    {
                        state?.error_message && <span className="text-red-700 flex-none content-center mr-4">{state.error_message}</span>
                    }
                </div>
            </form>
        </>
    )
}